import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import SALink from 'components/SALink'
import FollowIcon from 'svg/follow.svg'
import FollowingIcon from 'svg/following.svg'
import HeaderConnector, { ConnectorProps as HeaderProps } from '../../connectors/Header'
import styles from './ArtistInfo.module.scss'
import ActionsConnector, { ConnectorProps as ActionsProps } from '../../connectors/Actions'
import FollowConnector, { ConnectorProps as FollowProps } from 'containers/Follow'
import { fetchFollowApi, updateUserFollowApi } from 'apis/private/follow'
import AuthenticationConnect, {
  ConnectorProps as AuthenticationProps,
} from 'connectors/Authentication'
import classNames from 'classnames'
import { capitalizeString } from 'lib/helpers'
import useIsMobile from 'hooks/useIsMobile'
import FilterIcon from 'svg/filter-v2.svg'
import FiltersChips from '../FiltersChips/FiltersChips'
import Skeleton from 'components/SA/Skeleton'
import useAvoidLayoutShift from 'hooks/useAvoidLayoutShift'
import Breadcrumb from 'components/SA/Breadcrumb'

type ArtistInfoProps = HeaderProps &
  ActionsProps &
  FollowProps &
  AuthenticationProps & {
    isFiltersVisible: boolean
    setIsFiltersVisible: Dispatch<SetStateAction<boolean>>
    onOpenMobileFilter: () => void
  }

const ArtistInfo = (props: ArtistInfoProps) => {
  const {
    isFiltersVisible,
    setIsFiltersVisible,
    avatarUrl,
    artistName,
    artistId,
    artistCountry,
    isUsersArtworksAccount,
    isSet,
    authenticated,
    openModal,
    userID,
    onOpenMobileFilter,
    facetsCount,
  } = props
  const [followedUserArray, setFollowedUserArray] = useState<ReadonlyArray<number>>([])
  const [isFollowHovered, setIsFollowHovered] = useState(false)
  const { isSkeletonVisible } = useAvoidLayoutShift()

  const handleMouseEnter = () => setIsFollowHovered(true)
  const handleMouseLeave = () => setIsFollowHovered(false)
  const isMobileScreen = useIsMobile()

  const fetchData = async () => {
    const result = await fetchFollowApi()
    setFollowedUserArray(result)
  }

  const capitalizedArtistName = capitalizeString(artistName)
  const isAvatarDefaultFile = avatarUrl.includes('/common/profile/avatar_tiny_square.jpg')

  const isArtistFollowed =
    authenticated && Array.isArray(followedUserArray) && followedUserArray.includes(artistId)

  const updateFollow = async ({
    userID,
    targetUserId,
    authenticated,
    following,
    openModal,
    callBack,
  }: Record<string, any>) => {
    if (!authenticated) {
      openModal('login')
    }

    const apiResponse = await updateUserFollowApi({
      userID,
      targetUserId,
      following,
    })

    if (apiResponse) {
      callBack(targetUserId)
    }
  }

  const handleFollow = () => {
    if (!isArtistFollowed && window && window.dataLayer) {
      window.dataLayer.push({
        event: 'follow',
      })
    }

    updateFollow({
      userID,
      targetUserId: artistId,
      authenticated,
      following: !isArtistFollowed,
      openModal,
      callBack: () => {
        if (window && window.dataLayer) {
          if (!isArtistFollowed) {
            window.dataLayer.push({
              event: 'follow success',
            })
          } else {
            window.dataLayer.push({
              event: 'unfollow',
            })
          }
        }

        isArtistFollowed
          ? setFollowedUserArray(followedUserArray.filter((item) => item !== artistId))
          : setFollowedUserArray([...followedUserArray, artistId])
      },
    })
  }

  const handleToggleFiltersVisibility = () => {
    if (isMobileScreen) {
      onOpenMobileFilter()
    } else {
      setIsFiltersVisible((prevValue) => !prevValue)
    }
  }

  useEffect(() => {
    if (authenticated && !isUsersArtworksAccount) {
      fetchData()
    }
  }, [authenticated])

  return (
    <div className={styles.artistInfoHeaderWrapper}>
      <Breadcrumb
        items={[
          {
            name: capitalizedArtistName,
            title: `View profile of ${capitalizedArtistName} breadcrumb`,
            url: `/account/profile/${artistId}`,
          },
          {
            name: 'All Works',
            title: `${capitalizedArtistName} all Works`,
          }
        ]}
      />

      <div className={styles.artistInfoHeaderContainer}>
        <div className={styles.artistInfoHeaderBox}>
          <div className={styles.nameCountryBox}>
            <SALink
              href={`/account/profile/${artistId}`}
              title={`View profile of ${capitalizedArtistName}`}
              className={styles.artistInfoHeaderAvatarBox}
            >
              <img
                className={classNames(styles.artistInfoHeaderAvatarImage, {
                  [styles.artistInfoHeaderAvatarImageVisible]: !isAvatarDefaultFile,
                })}
                src={avatarUrl}
                alt={capitalizedArtistName}
              />
              <h1 className='defaultTypographyH4' data-type='artist-name'>
                {capitalizedArtistName} Artworks
              </h1>
            </SALink>
            {artistCountry && (
              <div className={styles.artistCountryBox}>
                <span>{artistCountry}</span>
              </div>
            )}
          </div>

          <div className={styles.artistInfoHeaderActionsContainer}>
            {isSet ? (
              <>
                {isUsersArtworksAccount ? (
                  <>
                    <SALink
                      href='/studio'
                      title='Manage artworks'
                      className={styles.artistInfoHeaderActionsLink}
                    >
                      MANAGE ARTWORKS
                    </SALink>
                    <SALink
                      href='/studio/upload'
                      title='Upload your artwork'
                      className={styles.artistInfoHeaderActionsLink}
                    >
                      UPLOAD ART
                    </SALink>
                  </>
                ) : (
                  <>
                    <SALink
                      href={`/account/profile/${artistId}`}
                      title={`View profile of ${capitalizedArtistName}`}
                      className={styles.artistInfoHeaderActionsLink}
                    >
                      VIEW PROFILE
                    </SALink>
                    <div
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      className={classNames(styles.artistInfoHeaderActionsFollowBox, {
                        [styles.artistInfoHeaderActionsFollowBoxHovered]:
                          isArtistFollowed && isFollowHovered,
                      })}
                    >
                      {isArtistFollowed ? <FollowingIcon /> : <FollowIcon />}

                      <button
                        onClick={handleFollow}
                        className={styles.artistInfoHeaderActionsButton}
                        title={`${
                          isArtistFollowed ? 'Unfollow' : 'Follow'
                        } ${capitalizedArtistName}`}
                        rel='nofollow'
                      >
                        {isArtistFollowed ? (isFollowHovered ? 'UNFOLLOW' : 'FOLLOWING') : 'FOLLOW'}
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                <Skeleton width='165px' height='24px' />
                <Skeleton width='165px' height='24px' />
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.filtersSelectedContainer}>
        {isSkeletonVisible ? (
          <Skeleton width='160px' height='40px' />
        ) : (
          <button onClick={handleToggleFiltersVisibility} className={styles.headerFilterButton}>
            <FilterIcon />
            <span>
              {!isFiltersVisible || isMobileScreen ? 'Filters' : 'Hide Filters'}{' '}
              {facetsCount ? ` (${facetsCount})` : ``}
            </span>
          </button>
        )}
        <FiltersChips />
      </div>
    </div>
  )
}

export default HeaderConnector(ActionsConnector(FollowConnector(AuthenticationConnect(ArtistInfo))))
